/**
 * HR-portal FTUE — Exit Guide document steps (`/exit-guide-documents`).
 *
 * Owns the on-page coachmarks of the `portal-hr-activation` tour that land on
 * the Exit Guide Documents page:
 *
 *  - `exitGuideUpload` — the visible dashed upload control. Its own phase (one
 *    concept). State-independent: the upload card renders regardless of how
 *    many documents exist, so this step's condition never looks at the
 *    document list.
 *  - `exitGuideLibraryIntro` / `exitGuideRowActions` — a 2-step group sharing
 *    `PORTAL_HR_PHASE.exitGuideLibrary` (chromes as "1 of 2" / "2 of 2",
 *    mirroring the Learn tour's empty-branch group in `use-learn-ftue`). The
 *    Document library card only renders once at least one document exists, so
 *    both steps gate on `hasDocuments`.
 *
 * Mounted from `PortalExitGuideDocumentsPage`. The layout hook
 * (`use-portal-hr-ftue`) owns the flow start and the nav step that leads here;
 * this hook only adds the follow-up steps (order x2-x4) so their `condition`s
 * can close over the page's live state, matching the job-seeker pattern
 * (`use-opportunities-ftue`, `use-learn-ftue`).
 *
 * The upload anchor is the single upload control (a styled `<label>` that
 * opens the file picker), so that step IS `dismissOnAction`: clicking it is
 * the admin acting on the intent and completes the step. The explicit "Got
 * it" ack stays visible too (mirrors the resume score-card step). The library
 * pair anchors on the Document library card header and the table's static
 * "Actions" column header rather than any one row — a first-time org's only
 * document may be Pelgo's own default doc, whose row hides its own
 * toggle/delete, which would make a row-button anchor fragile.
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_PHASE,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

export interface PortalExitGuideFtueArgs {
  /** Optional gate: hold the coachmark until the page's document list has loaded
   *  (`!isLoading`). The `<h1>` and upload card render regardless, so this
   *  defaults to `true` — pass `false` only to defer the nudge past the fetch. */
  ready?: boolean
  /** At least one document exists (the Document library card is rendered).
   *  Gates the library-intro / row-actions pair, which have no anchor to land
   *  on otherwise. Defaults to `false`. */
  hasDocuments?: boolean
}

const EXIT_GUIDE_PATH = '/exit-guide-documents'

/** This hook's own step ids, for the re-eval effect below. */
const OWN_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.exitGuideUpload,
  PORTAL_HR_STEP.exitGuideLibraryIntro,
  PORTAL_HR_STEP.exitGuideRowActions,
]

export function usePortalExitGuideFtue({
  ready = true,
  hasDocuments = false,
}: PortalExitGuideFtueArgs = {}) {
  const uploadAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.exitGuideUpload)
  const libraryIntroAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.exitGuideLibrary)
  const rowActionsAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.exitGuideRowActions)

  // Live page state for the step `condition`s (registered once below, so the
  // closures must read the current value rather than the mount-time snapshot).
  const readyRef = useRef(ready)
  readyRef.current = ready
  const hasDocumentsRef = useRef(hasDocuments)
  hasDocumentsRef.current = hasDocuments

  // Register the on-page steps once. Registration is idempotent (keyed by id)
  // and does not itself trigger evaluation.
  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.exitGuideUpload,
        phase: PORTAL_HR_STEP.exitGuideUpload,
        order: PORTAL_HR_ORDER.exitGuideUpload,
        trigger: { type: 'immediate' },
        // Only a candidate on `/exit-guide-documents`, where the upload control is
        // rendered. Off-route it is skipped so an immediate step whose anchor is
        // absent never blocks the rest of the flow (see the note in
        // `use-resume-upload-ftue`).
        condition: () => onPortalPath(EXIT_GUIDE_PATH) && readyRef.current,
        anchorId: PORTAL_HR_ANCHOR.exitGuideUpload,
        placement: 'bottom',
        // The anchor is the single upload control (a styled `<label>` that opens
        // the file picker): clicking it is the admin acting on the intent, so
        // complete the step on that click rather than leaving the coachmark
        // hanging. The explicit `dismissLabel` keeps the "Ok" ack visible too.
        dismissOnAction: true,
        content: {
          body: 'Upload your RA or exit-guide PDF here. Employees get answers drawn from it in benefits chat.',
          dismissLabel: 'Ok',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.exitGuideLibraryIntro,
        // Shared phase with exitGuideRowActions below: a 2-step group that only
        // exists once documents exist, so it chromes as "1 of 2" (mirrors the
        // Learn tour's empty-branch group in use-learn-ftue). skipScope stays
        // the portalStep() default ('phase'), so skipping this pair retires only
        // the pair, not the (separately-phased, already-seen) upload step.
        phase: PORTAL_HR_PHASE.exitGuideLibrary,
        order: PORTAL_HR_ORDER.exitGuideLibraryIntro,
        trigger: { type: 'immediate' },
        condition: () =>
          onPortalPath(EXIT_GUIDE_PATH) && readyRef.current && hasDocumentsRef.current,
        anchorId: PORTAL_HR_ANCHOR.exitGuideLibrary,
        placement: 'bottom',
        content: {
          body: "Every file you upload lands here once it's processed.",
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.exitGuideRowActions,
        phase: PORTAL_HR_PHASE.exitGuideLibrary,
        order: PORTAL_HR_ORDER.exitGuideRowActions,
        trigger: { type: 'immediate' },
        condition: () =>
          onPortalPath(EXIT_GUIDE_PATH) && readyRef.current && hasDocumentsRef.current,
        anchorId: PORTAL_HR_ANCHOR.exitGuideRowActions,
        placement: 'bottom',
        content: {
          body: "View the PDF, toggle a document active or inactive, or delete ones you no longer need. Pelgo's own default guide can't be edited.",
          dismissLabel: 'Ok',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — start the flow so the
    // engine re-picks the candidate now this step exists. Idempotent; the layout
    // hook (`use-portal-hr-ftue`) owns the real start logic.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when the page state changes (a plain re-render doesn't re-run the
  // engine). If one of this hook's own steps is active but its `condition` has
  // flipped false and it hasn't been acknowledged yet, mark it seen so the tour
  // advances cleanly instead of the coachmark just vanishing. Otherwise just
  // re-pick the candidate. Mirrors `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: ready/hasDocuments intentionally re-trigger evaluation; the effect reads live state via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && OWN_STEP_IDS.includes(activeStepId)) {
      const s = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (s?.condition && !s.condition() && !seen.has(activeStepId)) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [ready, hasDocuments])

  return {
    /** Spread onto the visible `<label htmlFor="exit-guide-file-upload">`. */
    uploadAnchor,
    /** Spread onto the Document library `CardHeader`. */
    libraryIntroAnchor,
    /** Spread onto the table's static "Actions" `<th>`. */
    rowActionsAnchor,
  }
}
